import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, type AuditEvent, type AuditLogQuery, type AuditResponse } from '../api.ts';
import { message, plural } from '../format.ts';
import './audit.css';

/**
 * The audit trail — what was done in this deployment, newest first.
 *
 * Every mutation that matters records an event server-side; this screen reads
 * them back. `actorLabel` is the server's own words for who acted (a name, or
 * the break-glass token), so the console never reconstructs an actor from an id.
 * `detail` is an open object whose shape depends on the action, and it is shown
 * verbatim as monospace JSON rather than interpreted — the console does not
 * decide what an event means.
 *
 * Paging is keyset, not offset: "Load older" passes the id of the last row
 * already shown as the `before` cursor, so a trail that is being written to
 * while it is read does not skip or repeat a row the way an offset would.
 */

const PAGE = 50;

const WHEN = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const formatWhen = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : WHEN.format(at);
};

interface Filters {
  readonly action: string;
  readonly from: string;
  readonly to: string;
}

const EMPTY: Filters = { action: '', from: '', to: '' };

/** Only the filters the server takes, and only when set. */
const toQuery = (filters: Filters, before?: string) => ({
  ...(filters.action.trim() !== '' ? { action: filters.action.trim() } : {}),
  ...(filters.from !== '' ? { from: filters.from } : {}),
  ...(filters.to !== '' ? { to: filters.to } : {}),
  ...(before !== undefined ? { before } : {}),
  limit: PAGE,
});

interface AuditProps {
  /**
   * Where the trail comes from. The console reads its own deployment's; the
   * platform area passes the deployment-wide read. Everything else on the
   * screen — filters, paging, the expanded detail — is the same screen.
   */
  readonly read?: (query: AuditLogQuery) => Promise<AuditResponse>;
  /** The sentence above the filters, saying whose trail this is. */
  readonly lead?: ReactNode;
}

const CONSOLE_LEAD =
  'What has been done in this deployment — who acted, on what, and when. Newest first. Everyone who runs this deployment can read its own trail.';

export const Audit = ({ read = api.auditLog, lead = CONSOLE_LEAD }: AuditProps) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback(async (active: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const { events: rows } = await read(toQuery(active));
      setEvents(rows);
      setExhausted(rows.length < PAGE);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setLoading(false);
    }
  }, [read]);

  useEffect(() => {
    void load(filters);
  }, [load, filters]);

  const loadOlder = async () => {
    const last = events[events.length - 1];
    if (last === undefined) return;
    setBusy(true);
    setError(null);
    try {
      const { events: rows } = await read(toQuery(filters, last.id));
      setEvents((current) => [...current, ...rows]);
      setExhausted(rows.length < PAGE);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setExpanded(new Set());
    setFilters(draft);
  };

  const clearFilters = () => {
    setDraft(EMPTY);
    setExpanded(new Set());
    setFilters(EMPTY);
  };

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  const filtered = filters.action !== '' || filters.from !== '' || filters.to !== '';

  return (
    <div className="audit-screen">
      <p className="lead">{lead}</p>

      <form className="toolbar au-filters" onSubmit={applyFilters} noValidate>
        <div className="field au-field">
          <label htmlFor="au-action">Action</label>
          <input
            id="au-action"
            type="text"
            autoComplete="off"
            placeholder="e.g. tenants.archived"
            value={draft.action}
            onChange={(event) => setDraft((current) => ({ ...current, action: event.target.value }))}
          />
        </div>
        <div className="field au-field">
          <label htmlFor="au-from">From</label>
          <input
            id="au-from"
            type="date"
            value={draft.from}
            onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
          />
        </div>
        <div className="field au-field">
          <label htmlFor="au-to">To</label>
          <input
            id="au-to"
            type="date"
            value={draft.to}
            onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
          />
        </div>
        <div className="au-filter-actions">
          <button type="submit" className="btn btn-primary btn-sm">
            Apply
          </button>
          {filtered && (
            <button type="button" className="btn btn-quiet btn-sm" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>
      </form>

      {error !== null && <div className="error au-error">{error}</div>}

      {loading ? (
        <p className="note" aria-live="polite">
          Reading the trail…
        </p>
      ) : events.length === 0 ? (
        <div className="empty au-empty">
          <span className="empty-title">
            {filtered ? 'No events match those filters' : 'Nothing has been recorded yet'}
          </span>
          <p className="empty-body">
            {filtered
              ? 'Widen the action or the date range, or clear the filters to see the whole trail.'
              : 'Every archive, purge, role change and sweep is recorded here as it happens. The trail fills in as the deployment is used.'}
          </p>
        </div>
      ) : (
        <>
          <p className="status-inline au-count" role="status">
            {plural(events.length, 'event')} shown{exhausted ? '' : ' so far'}.
          </p>

          <div className="tbl-wrap au-wrap">
            <table className="tbl au-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">Action</th>
                  <th scope="col">Subject</th>
                  <th scope="col">IP</th>
                  <th scope="col">Detail</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <AuditRow
                    key={event.id}
                    event={event}
                    open={expanded.has(event.id)}
                    onToggle={() => toggle(event.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {!exhausted && (
            <div className="toolbar au-more">
              <button
                type="button"
                className="btn btn-quiet"
                aria-busy={busy || undefined}
                disabled={busy}
                onClick={() => void loadOlder()}
              >
                {busy ? 'Loading…' : 'Load older'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface AuditRowProps {
  readonly event: AuditEvent;
  readonly open: boolean;
  readonly onToggle: () => void;
}

const AuditRow = ({ event, open, onToggle }: AuditRowProps) => {
  const hasDetail = Object.keys(event.detail).length > 0;
  const panelId = `au-detail-${event.id}`;

  return (
    <>
      <tr className="au-row">
        <td className="num au-when">{formatWhen(event.at)}</td>
        <td className="au-who">{event.actorLabel}</td>
        <td>
          <span className="mono au-action">{event.action}</span>
        </td>
        <td className="mono au-subject">{event.subject ?? '—'}</td>
        <td className="mono au-ip">{event.ip ?? '—'}</td>
        <td>
          {hasDetail ? (
            <button
              type="button"
              className="btn btn-quiet btn-sm"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={onToggle}
            >
              {open ? 'Hide' : 'Show'}
            </button>
          ) : (
            <span className="note">—</span>
          )}
        </td>
      </tr>
      {open && hasDetail && (
        <tr className="au-detail-row">
          <td colSpan={6}>
            <pre className="mono au-detail" id={panelId}>
              {JSON.stringify(event.detail, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};
